import "./elevationGate.scss";
import { BRAND } from "constants/brand";
import { useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Form } from "react-bootstrap";
import { Ring } from "@uiball/loaders";
import { Toast } from "primereact/toast";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLock } from "@fortawesome/free-solid-svg-icons";

import { verifyAdminPassword } from "services/user";
import { isStatementsUnlocked, markStatementsUnlocked } from "utils/auth";

/**
 * Wraps the statements pages with a one-shot password prompt for admin
 * users. State is local + ephemeral on purpose:
 *
 * - The unlock is a module-level flag in utils/auth, not Redux and not
 *   localStorage / sessionStorage, so it lives exactly as long as the page
 *   does: a reload, a new tab or a closed browser all re-prompt.
 * - ONE prompt covers the whole section. The list page and the detail page
 *   each mount their own gate, so while this lived in component state,
 *   opening a statement from the list asked for the password a second time
 *   — one navigation, two prompts, for the same boundary already cleared.
 * - Logout clears it (clearStoredAuth), so an unattended dashboard cannot be
 *   walked up to and reopened without the password.
 *
 * Vendor users skip the gate — their data is already auto-scoped to
 * their own statements server-side, and asking them to re-enter a
 * password to see their own settlements would just be friction.
 */
const ElevationGate = ({ children }) => {
    const adminInfo = useSelector((state) => state.auth.userData);
    const isVendor = adminInfo?.role === "vendor";
    const toast = useRef(null);

    const [unlocked, setUnlocked] = useState(isStatementsUnlocked);
    const [password, setPassword] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [errorMsg, setErrorMsg] = useState(null);

    if (isVendor || unlocked) {
        return children;
    }

    const showMessage = (severity, summary, detail) => {
        toast.current?.show({ severity, summary, detail, life: 3000 });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!password) {
            setErrorMsg("الرجاء إدخال كلمة المرور");
            return;
        }
        setSubmitting(true);
        setErrorMsg(null);
        try {
            await verifyAdminPassword(password);
            setPassword("");
            markStatementsUnlocked();
            setUnlocked(true);
        } catch (err) {
            const msg = err?.response?.data?.message || "كلمة المرور غير صحيحة";
            setErrorMsg(msg);
            showMessage("error", "تعذر الدخول", msg);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="elevation-gate">
            <Toast style={{ direction: "rtl" }} ref={toast} position="top-left" />

            <div className="elevation-gate__card">
                <h4 className="elevation-gate__title">كشوف الحسابات</h4>
                <p className="elevation-gate__copy">
                    هذه الصفحة محمية. الرجاء إدخال كلمة المرور للمتابعة.
                </p>

                <Form onSubmit={handleSubmit}>
                    <Form.Group className="mb-3">
                        <Form.Label>
                            كلمة المرور<span style={{ color: "red" }}>*</span>
                        </Form.Label>
                        <Form.Control
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            autoFocus
                            disabled={submitting}
                        />
                    </Form.Group>

                    {errorMsg ? <div className="elevation-gate__error">{errorMsg}</div> : null}

                    <button
                        type="submit"
                        disabled={submitting}
                        style={{
                            backgroundColor: BRAND,
                            color: "#fff",
                            width: "100%",
                            padding: "10px",
                            border: "none",
                            borderRadius: "6px",
                            opacity: submitting ? 0.6 : 1,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: "8px",
                        }}
                    >
                        {submitting ? (
                            <Ring size={18} lineWeight={3} speed={2} color="#fff" />
                        ) : (
                            <>
                                <FontAwesomeIcon icon={faLock} />
                                دخول
                            </>
                        )}
                    </button>
                </Form>
            </div>
        </div>
    );
};

export default ElevationGate;
